import { Group, Text } from '@mantine/core';
import { SparkleIcon } from '@phosphor-icons/react';
import cx from 'classnames';
import { useEffect, useState } from 'react';

import classes from '../chat.module.css';
import { ChatStatusHelpers } from '../constants';
import {
  getToolIcon,
  parseToolCallMessage,
  getToolDisplayName,
  type ToolFriendlyNamesMap,
  type ToolRegistry,
} from '../utils/tool-calling-utils';

import type { ChatStatus } from '../types';
import type { UIMessage } from 'ai';

type ToolCallIndicatorProps = {
  message: UIMessage;
  status?: ChatStatus;
  isCurrentMessage?: boolean;
  toolFriendlyNames?: ToolFriendlyNamesMap;
  toolRegistry?: ToolRegistry;
};

const MIN_DISPLAY_DURATION = 1500;
const TRANSITION_DURATION = 250;

export default function ToolCallIndicator({
  message,
  status,
  isCurrentMessage = false,
  toolFriendlyNames,
  toolRegistry,
}: ToolCallIndicatorProps) {
  const [isIconTransitioning, setIsIconTransitioning] = useState(false);
  const [isTextTransitioning, setIsTextTransitioning] = useState(false);
  const [displayToolId, setDisplayToolId] = useState<string | null>(null);
  const [displayText, setDisplayText] = useState('');
  const [lastIconUpdateTime, setLastIconUpdateTime] = useState<number>(
    Date.now(),
  );
  const [lastTextUpdateTime, setLastTextUpdateTime] = useState<number>(
    Date.now(),
  );

  const {
    latestToolPart,
    isCompleted,
    isPreparing,
    isLastToolBeforeGeneration,
    isWaitingForResponse,
    currentToolId,
    currentText,
  } = parseToolCallMessage(message, toolRegistry, toolFriendlyNames);

  useEffect(() => {
    if (currentToolId === displayToolId) return;

    const timeSinceLastUpdate = Date.now() - lastIconUpdateTime;
    const remainingTime = Math.max(
      0,
      MIN_DISPLAY_DURATION - timeSinceLastUpdate,
    );

    let transitionTimer: ReturnType<typeof setTimeout> | null = null;

    const delayTimer = setTimeout(() => {
      setIsIconTransitioning(true);

      transitionTimer = setTimeout(() => {
        setDisplayToolId(currentToolId);
        setIsIconTransitioning(false);
        setLastIconUpdateTime(Date.now());
      }, TRANSITION_DURATION);
    }, remainingTime);

    return () => {
      clearTimeout(delayTimer);
      if (transitionTimer) clearTimeout(transitionTimer);
    };
  }, [currentToolId, displayToolId, lastIconUpdateTime]);

  useEffect(() => {
    if (currentText === displayText) return;

    const timeSinceLastUpdate = Date.now() - lastTextUpdateTime;
    const remainingTime = Math.max(
      0,
      MIN_DISPLAY_DURATION - timeSinceLastUpdate,
    );

    let transitionTimer: ReturnType<typeof setTimeout> | null = null;

    const delayTimer = setTimeout(() => {
      setIsTextTransitioning(true);

      transitionTimer = setTimeout(() => {
        setDisplayText(currentText);
        setIsTextTransitioning(false);
        setLastTextUpdateTime(Date.now());
      }, TRANSITION_DURATION);
    }, remainingTime);

    return () => {
      clearTimeout(delayTimer);
      if (transitionTimer) clearTimeout(transitionTimer);
    };
  }, [currentText, displayText, lastTextUpdateTime]);

  if (!isPreparing && !latestToolPart) return null;

  if (!isCurrentMessage) return null;

  if (
    isCurrentMessage &&
    status &&
    !ChatStatusHelpers.isProcessing(status) &&
    !isCompleted
  ) {
    return null;
  }

  const IconComponent = displayToolId
    ? getToolIcon(displayToolId, toolRegistry) || SparkleIcon
    : SparkleIcon;

  return (
    <Group gap="xs" align="flex-start" mb="sm">
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          paddingTop: '2px',
          transition: 'opacity 0.25s ease-in-out, transform 0.25s ease-in-out',
          opacity: isIconTransitioning ? 0 : 1,
          transform: isIconTransitioning ? 'translateY(4px)' : 'translateY(0)',
        }}
      >
        <IconComponent
          size={16}
          weight="bold"
          color={
            isCompleted && !isWaitingForResponse
              ? 'var(--mantine-color-green-6)'
              : 'var(--mantine-primary-color-7)'
          }
        />
      </div>
      <Text
        size="sm"
        fw={600}
        c="black"
        className={cx('chromatic-ignore', {
          [classes.toolCallShimmer]: !isCompleted || isWaitingForResponse,
        })}
        style={{
          transition: 'opacity 0.25s ease-in-out',
          opacity: isTextTransitioning ? 0 : 1,
        }}
      >
        {displayText ||
          (displayToolId
            ? getToolDisplayName(
                `tool-${displayToolId}`,
                toolRegistry,
                toolFriendlyNames,
              )
            : null) ||
          'Preparing'}
        {!isLastToolBeforeGeneration &&
          status &&
          ChatStatusHelpers.isProcessing(status) && (
            <span className={classes.ellipsisIndicator} />
          )}
      </Text>
    </Group>
  );
}
